import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import * as L from 'leaflet';
import { ILivePosition, IPosition, IGetMapBoundary } from '../../api';
import { SharedService, TranslationKey } from '..';
import { ILatLng, MapViewService } from './interfaces';
import 'leaflet-arrowheads';

@Injectable({
  providedIn: 'root'
})
export class LeafletMapViewService implements MapViewService {
  private map!: L.Map;
  initialMarker:L.CircleMarker;
  endMarker:L.CircleMarker;
  rendezvousMarkerLayer:L.LayerGroup<any>;
  zoomClick:boolean = false;
  disableMapMoved:boolean=false;
  private circleRadius:number;
  private callbackMoved: () => void;

  constructor(private sharedSvc: SharedService, private transSvc: TranslocoService) {
  }

  MapMoved(fn: () => void) {
    this.callbackMoved = fn;
  }

  createMap(url: string, target: string | HTMLElement, className: string): void {
    const u = new URL(url);
    const attribution = `Map data &copy; <a href="${u.origin}">${u.origin}</a>`;
    // { preferCanvas: true } uses canvas to render paths, otherwise it uses SVGs which can be very slow for long paths
    this.map = L.map(target, { preferCanvas: true }).setView([31.1642, 10.4541], 2);
    L.tileLayer(url, {
      attribution: attribution,
      maxZoom: 19,
      tileSize: 256,
      className: className,
      // zoomOffset: -1
    }).addTo(this.map);
    L.control.scale().addTo(this.map);
    this.map.on('zoomend', () =>this.handleZoomChange());
    this.map.on('moveend', () => {
      if (!this.disableMapMoved && target === 'analysis-map')
        this.onMapMoveEnd();
      });
  }

  getCurrentZoomLevel():number {
    return (this.map.getZoom());
  }

  updateMapSize() {
    setTimeout(() => {
      this.map.invalidateSize();
    }, 50);
  }

  addPolylineLayer(latLons: ILatLng[], color: string): unknown {
    const layer = L.polyline(latLons, { color: color ,smoothFactor: 5});
    layer.arrowheads({size: '15px', frequency: 'allvertices' ,yawn: 40, color: color});
    layer.addTo(this.map);
    return layer;
  }

  setPolylineLayerPositions(layer: unknown, latLons: ILatLng[]): void {
    const lLayer = layer as L.Polyline;
    lLayer.setLatLngs(latLons);
  }


  removeLayer(layer: unknown): void {
    const lLayer = layer as L.Layer;
    lLayer.clearAllEventListeners();
    this.map.closePopup();
    this.map.removeLayer(lLayer);
  }
  removeallLayer():void{
this.map.eachLayer(layer => {
  layer.clearAllEventListeners();
  this.map.removeLayer(layer);
});
return;
  }

  changePathLayerColor(pathLayer: unknown, color: string): void {
    const lPath = pathLayer as L.Polyline;
    lPath.setStyle({ color: color });
    lPath.arrowheads({size: '15px', frequency: 'allvertices' ,yawn: 40, color: color});
    lPath.redraw();
  }

  changeGroupLayerColor(layer: unknown, color: string): void {
    const lFeature = layer as L.LayerGroup;
    lFeature.eachLayer(x => {
      if((x as any).options.radius !== 8){
        (x as any).setStyle({ color: color });
      }
    });
  }

  addPositionMarker(livePosition: ILivePosition,mapType:string): unknown {
    const marker = L.marker([livePosition.lat, livePosition.long]);
    // No popup is bound: details moved to submenu.
    marker.addTo(this.map);
    return marker;
  }

  modifyPositionMarker(marker: unknown, livePosition: ILivePosition, mapType?:string): void {
    const lMarker = marker as L.Marker;
    lMarker.setLatLng({ lat: livePosition.lat, lng: livePosition.long });
  }

  removePositionMarker(marker: unknown): void {
    (marker as L.Marker).remove();
  }

  updateMoveendStatus(value:boolean):void {
    this.disableMapMoved = value;
  }

  addCircleMarkersLayer(livePositions: ILivePosition[], color: string,mapType:string): unknown {
    const markers: L.Layer[] = [];
    const paramColor: string = color;
    const startColor: string = '#00cc66';
    const endColor: string = '#0099cc';
    var firstMarker:L.CircleMarker| null;
    var lastMarker :L.CircleMarker|null;
    let mapType_display = mapType.toString();
    this.map.closePopup();
    for (let [index,livePosition] of livePositions.entries()) {
      if((index === 0) ||((livePositions.length === 0) && (mapType_display !== 'Realtime Data'))){
        color = startColor;
        var indexValue='first';
      }
      else if((index === livePositions.length - 1) && (mapType_display !== 'Realtime Data')){
        color = endColor;
        var indexValue='last';
      }
      else{
        color = paramColor;
        var indexValue='inter';
      }
      const marker = this.createCircleMarker(livePosition, color,indexValue, mapType_display);
      if(index === 0){
        firstMarker = marker;
        const options = firstMarker.options as ICircleMarkerOptionsWithCustomData;
        const livePosition = options.customData.position;
        const popupContent = this.getPositionMarkerPopupContent(livePosition,'Start',mapType_display);
        firstMarker.bindPopup(popupContent, {closeButton: false, autoPan: false,autoClose: false}).openPopup();
      }
      else if (index === livePositions.length - 1){
        lastMarker = marker;
        const options = lastMarker.options as ICircleMarkerOptionsWithCustomData;
        const livePosition = options.customData.position;
        const popupContent = this.getPositionMarkerPopupContent(livePosition,'End',mapType_display);
        lastMarker.bindPopup(popupContent, {closeButton: false, autoPan: false,autoClose: false}).openPopup();
      }
      else{
      marker.addEventListener('click', ev => {
        const marker = ev.target as L.CircleMarker<any>;
        const options = marker.options as ICircleMarkerOptionsWithCustomData;
        const livePosition = options.customData.position;
        const popupContent = this.getPositionMarkerPopupContent(livePosition,undefined,mapType_display);
        marker.bindPopup(popupContent, { closeButton: false, autoPan: false,autoClose: false}).openPopup().unbindPopup();
      });
      }
      // *** Hide popup on map click solution - work efficenitly for small no of marker ***
      // *** Not reccomended - it will delay display of popup on marker click if too many maker is added to map ***
      // this.map.addEventListener('click', ev=>{
      //   firstMarker?.openPopup();
      //   lastMarker?.openPopup();
      // });
      markers.push(marker);
    }
    const layer = L.layerGroup(markers);
    layer.addTo(this.map);
    if(this.initialMarker && this.endMarker){
      this.initialMarker.bringToFront();
      this.endMarker.bringToFront();
    }
    return layer;
  }

  getMapBoundary(): IGetMapBoundary {
    var bounds = this.map.getBounds();
    // Extract the coordinates of the boundaries
    var southWest = bounds.getSouthWest();
    var northEast = bounds.getNorthEast();
    var boundary :IGetMapBoundary = {northEast:{
      lat:northEast.lat,
      lng:northEast.lng,
    },southWest: {
      lat:southWest.lat,
      lng:southWest.lng,
    }};
    return boundary;
  }

  handleZoomChange(){
    this.zoomClick = true;
    setTimeout(() => {
      if(this.initialMarker && this.endMarker){
        this.initialMarker.bringToFront();
        this.endMarker.bringToFront();
      }
      if((this.rendezvousMarkerLayer) && this.map.hasLayer(this.rendezvousMarkerLayer)){
        var currentZoomLevel = this.map.getZoom();
        const layerFeature = this.rendezvousMarkerLayer as L.LayerGroup;
        layerFeature.eachLayer(layerele => {
          var radius = 6 * this.circleRadius / (Math.pow(2, 19 - currentZoomLevel));
          if (radius < currentZoomLevel * 2)
          radius = currentZoomLevel * 2;
          if((layerele as any).options.radius){
            (layerele as any).setStyle({radius : radius});
          }
        });
      }
    }, 200);
  }

  onMapMoveEnd() {
    if (this.callbackMoved != undefined)
      this.callbackMoved();
  }

  addCircleMarker(livePosition: ILivePosition, color: string, layerGroup: unknown, mapType: string): unknown {
    const startColor: string = '#00cc66';
    let indexValue;
    let firstMarker:L.CircleMarker| null;
    const lLayerGroup = layerGroup as L.LayerGroup<any>;
    if(lLayerGroup.getLayers().length === 0){
      color = startColor;
      indexValue ='first';
    }
    const marker = this.createCircleMarker(livePosition, color,indexValue);
    if(lLayerGroup.getLayers().length === 0){
      firstMarker = marker;
      const options = firstMarker.options as ICircleMarkerOptionsWithCustomData;
      const livePosition = options.customData.position;
      const popupContent = this.getPositionMarkerPopupContent(livePosition,'Start', mapType.toString());
      firstMarker.bindPopup(popupContent, {closeButton: false, autoPan: false,autoClose: false}).openPopup();
    }
    marker.addTo(lLayerGroup);
    if(this.initialMarker){
      this.initialMarker.bringToFront();
    }
    return marker;
  }

  removeCircleMarker(circleMarker: unknown): unknown {
    throw new Error('Method not implemented.');
  }

  async zoomToLayer(layer: unknown, userMapClick:boolean = false): Promise<void> {
    // TODO: What if the layer is not polyline ?
    const layerObj = layer as L.Polyline;
    const latLngs = layerObj.getLatLngs();
    await this.fitMapToLatLngs(latLngs as L.LatLng[]);
  }

  zoomToPositions(positions: IPosition[]): void {
    const latLngs: L.LatLng[] = positions.map(x => ({ lat: x.lat, lng: x.long } as L.LatLng));
    this.fitMapToLatLngs(latLngs);
  }

  zoomToPositionMarkers(markers: unknown[]): void {
    const lMarkers = markers as L.Marker[];
    const latLngs: L.LatLng[] = lMarkers.map(x => x.getLatLng());
    this.fitMapToLatLngs(latLngs);
  }

  zoomToPositionMarkersOperational(markers: unknown[], maxZoom: number = 17): void {
    const lMarkers = markers as L.Marker[];
    const latLngs: L.LatLng[] = lMarkers.map(x => x.getLatLng());
    if (latLngs.length === 0) {
      return;
    }
    if (latLngs.length === 1) {
      this.map.setView(latLngs[0], maxZoom);
    } else {
      const bounds = L.latLngBounds(latLngs);
      this.map.fitBounds(bounds, { maxZoom: maxZoom });
    }
  }

  destroyMap(): void {
    if (!this.map) {
      return;
    }
    // Remove everything including event handlers
    this.map.clearAllEventListeners();
    this.map.eachLayer(layer => {
      layer.clearAllEventListeners();
      this.map.removeLayer(layer);
    });
    this.map.remove();
  }

  async createRendezvousMarkerLayer(positions: any[], appliedRadius:number) {
    let currentZoomLevel = this.map.getZoom();
    let clusterLayer = L.layerGroup();
    this.circleRadius = appliedRadius;
    var radius = 6 * this.circleRadius / (Math.pow(2, 19 - currentZoomLevel));
    if (radius < currentZoomLevel * 2)
      radius = currentZoomLevel * 2;
    var markers = [];
    for (var trackIndex = 0; trackIndex < positions.length; trackIndex++) {
      let marker: L.CircleMarkerOptions = {};
      var position = positions[trackIndex];
      var rgbColor1:any[] = this.hexToRgb(position.track1.color);
      var rgbColor2:any[] = this.hexToRgb(position.track2.color);
      var rgbColor:any[] = [Math.round((rgbColor1[0]+rgbColor2[0])/2), Math.round((rgbColor1[1]+rgbColor2[1])/2),Math.round((rgbColor1[2]+rgbColor2[2])/2)];
      var color:string = this.rgbToHex(rgbColor[0], rgbColor[1], rgbColor[2]);
      for (var i = 0; i < position.midPoints.length; i++) {
        const latLngLiteral = this.toLatLng({ lat: position.midPoints[i].lat, lng: position.midPoints[i].lng });
        markers.push({ color: color, latLng: latLngLiteral });
      }
    }
    await markers.forEach(function (marker) {
    var circle = L.circleMarker(marker.latLng, {
      color: marker.color,
      radius: radius,
    }).addTo(clusterLayer);
  });
    this.rendezvousMarkerLayer = clusterLayer;
    this.map.addLayer(clusterLayer);
    return clusterLayer;
  }

  private hexToRgb(hexCode:string) {
    const hex = hexCode.replace('#', '');  // Remove leading '#'
    const red = parseInt(hex.substr(0, 2), 16);  // Convert red component from hexadecimal to decimal
    const green = parseInt(hex.substr(2, 2), 16);  // Convert green component from hexadecimal to decimal
    const blue = parseInt(hex.substr(4, 2), 16);  // Convert blue component from hexadecimal to decimal
    return [red, green, blue];
  }

  private rgbToHex(r:number, g:number, b:number) {
    // Convert each RGB component to its hexadecimal representation
    const redHex = r.toString(16).padStart(2, '0');
    const greenHex = g.toString(16).padStart(2, '0');
    const blueHex = b.toString(16).padStart(2, '0');
    // Combine the hexadecimal components
    const hexCode = `#${redHex}${greenHex}${blueHex}`;
    return hexCode;
  }

  private createCircleMarker(livePosition: ILivePosition, color: string,index?:string, mapType?:string): L.CircleMarker {
    var radius =5;
    if((index === 'first')||(index === 'last')){
      var radius=8;
    }
    const latLngLiteral = this.toLatLng({ lat: livePosition.lat, lng: livePosition.long });
    const options: ICircleMarkerOptionsWithCustomData = {
      radius: radius, color: color, customData: { position: livePosition }
    };
    if(index === 'first'){
      this.initialMarker = L.circleMarker(latLngLiteral, options);
      return this.initialMarker;
    }
    else if ((index === 'last') && (mapType !== 'Realtime Data')){
      this.endMarker = L.circleMarker(latLngLiteral, options);
      return this.endMarker;
    }
    else{
      const marker = L.circleMarker(latLngLiteral, options);
      return marker;
    }
  }

  private getPositionMarkerPopupContent(position: ILivePosition,positionDir?:string,mapType?:string): string {
    const speedTranslated = this.sharedSvc.escapeHTML(this.transSvc.translate(TranslationKey.speed));
    const latitudeTranslated = this.sharedSvc.escapeHTML(this.transSvc.translate(TranslationKey.latitude));
    const longitudeTranslated = this.sharedSvc.escapeHTML(this.transSvc.translate(TranslationKey.longitude));
    const courseTranslated = this.sharedSvc.escapeHTML(this.transSvc.translate(TranslationKey.course));
    const result =`<div style="font-weight: 700; font-size: 13px;">${this.sharedSvc.escapeHTML(position.paip)}</div>`
      + `<div style="font-weight: 700;">${this.sharedSvc.getFormattedDateTime(position.rdt).replace(/T/g, ' ').replace(/Z/g, '')+' (UTC)'}</div>`
      + `<div>${speedTranslated} = ${this.sharedSvc.escapeHTML('' + position.speed + ' km/h')}</div>`
      + `<div>${latitudeTranslated} = ${this.sharedSvc.escapeHTML('' + position.lat + ' N')}</div>`
      + `<div>${longitudeTranslated} = ${this.sharedSvc.escapeHTML('' + position.long + ' E')}</div>`
      + `<div style="display: flex; gap: 1em; align-items: center;">`
      + `<span>${courseTranslated} = ${this.sharedSvc.escapeHTML('' + position.course)}</span>`
      + `<img src="assets/leaflet/images/navigation-24x24.svg" style="transform: rotate(${position.course}deg)" />`
      + `</div>`
      + `<div style="display: flex; align-items: end; font-weight:500; color:grey; justify-content:flex-start; font-style: italic; font-size:12px margin-top:0.6em" >
      ${this.sharedSvc.escapeHTML(mapType + (positionDir?' - '+positionDir:''))}</div>`;
    return result;
  }

  private async fitMapToLatLngs(latLngs: L.LatLng[]): Promise<void> {
    if (latLngs.length === 0) {
      return;
    } else if (latLngs.length === 1) {
      await this.map.panTo(latLngs[0]);
    } else {
      const bounds = L.latLngBounds(latLngs);
      await this.map.fitBounds(bounds);
    }
  }

  // private toLatLngLiterals(latLons: ILatLng[]): L.LatLngLiteral[] {
  //   return latLons.map(x => this.toLatLng(x));
  // }

  private toLatLng(latLng: ILatLng): L.LatLngLiteral {
    return latLng as L.LatLngLiteral;
  }
}

interface ICircleMarkerOptionsWithCustomData extends L.CircleMarkerOptions {
  customData: { position: ILivePosition };
}
